import { JadeMask } from "@stone-payments/jade";
import { Store } from "@/actions/schemas/stores";

export function formatAddress({
  street,
  number,
  complement,
  district,
  zipCode,
  city,
  state,
}: {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
  zipCode?: string | null;
  city?: string | null;
  state?: string | null;
}) {
  const formattedZipCode = new JadeMask("99999-999").compile(zipCode || "");
  const address = [street, number, complement, district, formattedZipCode]
    .filter(Boolean)
    .join(", ");
  const location = [city, state].filter(Boolean).join(", ");

  if (address || location) {
    return {
      complete: [address, location].filter(Boolean).join(", "),
      grouped: {
        address,
        location,
      },
    };
  }

  return null;
}

const dayOrder = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "holidays",
] as const;

const dayAbbreviation: Record<(typeof dayOrder)[number], string> = {
  monday: "Seg",
  tuesday: "Ter",
  wednesday: "Qua",
  thursday: "Qui",
  friday: "Sex",
  saturday: "Sáb",
  sunday: "Dom",
  holidays: "Feriados",
};

const formatTime = (time: string): string => {
  const [hour, minute] = time.split(":");
  return minute === "00" ? `${hour}h` : `${hour}:${minute}h`;
};

const intervalsKey = (intervals: { openAt: string; closeAt: string }[]) =>
  intervals
    .map((interval) => `${interval.openAt}-${interval.closeAt}`)
    .join(",");

const formatIntervals = (intervals: { openAt: string; closeAt: string }[]) =>
  intervals
    .map(
      (interval) =>
        `${formatTime(interval.openAt)} às ${formatTime(interval.closeAt)}`
    )
    .join(", ");

export const formatOpeningHours = (
  days: NonNullable<Store["vitrine"]["openingHours"]>["days"]
): string => {
  const groups: {
    key: string;
    days: string[];
    intervals: { openAt: string; closeAt: string }[];
  }[] = [];

  dayOrder.forEach((day) => {
    const dayData = days[day];
    if (!dayData || dayData.closed || !dayData.intervals?.length) return;

    const key = intervalsKey(dayData.intervals);
    const group = groups.find((item) => item.key === key);

    if (group) group.days.push(dayAbbreviation[day]);
    else
      groups.push({
        key,
        days: [dayAbbreviation[day]],
        intervals: dayData.intervals,
      });
  });

  return groups
    .map(
      (group) => `${group.days.join(", ")}: ${formatIntervals(group.intervals)}`
    )
    .join("\n");
};
